import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { MusicVideoSongMapSection } from 'fym-shared/src/music24/musicVideoApi';
import { TypedMemo } from '../../../common/types';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { LoadingView } from '../../../common/views/LoadingView';
import { usageCounts } from '../../footage/footageView';
import { useSectionBankClips } from '../../footage/useSectionBankClips';
import { useMusicVideoEditor } from '../../MusicVideoEditorContext';
import { footageMessages } from '../../messages/footageMessages';
import { formatTime } from '../../timeline/timelineGeometry';
import { ShowcaseSearchBusy } from '../../showcase/ShowcaseSearchBusy';
import { ClipCard } from './ClipCard';
import { useAddClip } from './useAddClip';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * "For <section>" (design b1): the 12 clips that the director would pick for the section (in showcase
 * mode: the bank clips of the mood of the section).
 */
const BankSuggestionsComponent: React.FC<{ section: MusicVideoSongMapSection }> = ({ section }) => {
  const intl = useIntl();
  const { doc } = useMusicVideoEditor();
  const { add } = useAddClip();
  const suggest = useSectionBankClips(section);
  const { views } = suggest;
  const uses = useMemo(() => usageCounts(doc.clips), [doc.clips]);

  return (
    <>
      <div className={styles.sectionHead}>
        <b>{intl.formatMessage(footageMessages.forSection, { section: section.label })}</b>
        <small>
          {section.mood === null ? '' : `${section.mood} · `}
          {intl.formatMessage(footageMessages.forSectionFacts, {
            from: formatTime(section.startSec),
            to: formatTime(section.endSec),
          })}
        </small>
      </div>
      {suggest.isLoading && <LoadingView />}
      {suggest.isBusy && <ShowcaseSearchBusy />}
      {!suggest.isBusy && suggest.error !== undefined && !suggest.hasData && (
        <ErrorView error={suggest.error} retry={suggest.retry} />
      )}
      {views.length > 0 && (
        <div className={styles.clipGrid}>
          {views.map((view) => (
            <ClipCard key={view.key} view={view} uses={uses.get(view.key) ?? 0} onPick={add} />
          ))}
        </div>
      )}
    </>
  );
};

export const BankSuggestions = TypedMemo(BankSuggestionsComponent);
